/**
 * uploadIcons.tsx — glyphs for the "Your Uploads" accordion that are NOT part of the Figma port.
 *
 * Kept out of icons.tsx on purpose: that file's header declares itself a VERBATIM port from the
 * Design repo ("Imports nothing — no repoint"), so adding hand-drawn geometry there would quietly
 * invalidate its provenance claim. These are backend additions with no Figma source, so they get
 * their own file and say so.
 *
 * Conventions followed from icons.tsx: `currentColor` (a literal hex would fail audit-tokens.mjs),
 * `aria-hidden` + `focusable="false"` so the glyph never reaches the accessibility tree — at every
 * call site the meaning is already carried in adjacent TEXT, so nothing is conveyed by icon alone.
 * Geometry is drawn on a 24-unit grid with a stroked path, matching the visual weight of the
 * design-system's other small controls.
 */

interface IconProps {
  className?: string;
  /** Rendered size in px. */
  size?: number;
}

/**
 * Padlock — marks a submission the owner has WITHDRAWN from the catalogue.
 *
 * A closed padlock, because withdrawal is reversible (Profile.tsx's doWithdrawRestore) and reads as
 * "locked away", not "deleted". Shown at the panel's top-right. The withdrawn state is ALSO in the
 * panel's sublabel text ("Withdrawn from catalogue", submissionStatusLine), so this is a redundant
 * visual cue rather than the only signal — hence aria-hidden.
 */
export function PadlockIcon({ className = '', size = 14 }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* Body */}
      <rect x="4" y="10" width="16" height="11" rx="2" />
      {/* Shackle, closed */}
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

/**
 * Library / storage — the terminal "all your tools" tile's glyph. Supplied by the designer as
 * `storageVector.svg` (a Carbon-style "folder containing stacked units" mark: an open folder tab
 * behind a 2x2 block of drawers), replacing an earlier hand-drawn three-books-on-a-shelf.
 *
 * TWO CHANGES FROM THE SUPPLIED FILE, both required by this codebase:
 *
 *  1. THE ARTBOARD SCAFFOLDING IS DROPPED. The export carried the Figma PAGE around the icon, not
 *     just the icon: a full-bleed `#EEEEEE` rect, a 1392x9382 white canvas rect translated to
 *     (-1189,-1064), two large `#F4F4F4` frame paths, a `-93,-94 200x200` white card with a stroke,
 *     and a 1%-opacity 16x16 spacer. Pasted verbatim those paint an opaque grey square over the
 *     tile's teal fill and blow the drawn bounds out to ~10,000 units, so the 14x13 glyph would
 *     render as a speck. Only the five `#161616` paths are the mark; everything else is deleted.
 *
 *  2. `#161616` BECOMES `currentColor`. A literal hex here would fail audit-tokens.mjs, and the tile
 *     sets its own ink via `.ag-panel__icon { color: var(--text-on-accent) }` — so inheriting is both
 *     the house convention and what makes the glyph legible on the teal summary fill.
 *
 * The viewBox stays the source's `0 0 14 13` so the path data is untouched and needs no re-scaling;
 * `size` drives the rendered box as with every other icon here. This is a FILLED mark, not a stroked
 * one, so unlike its neighbours it takes `fill="currentColor"` and no stroke — the supplied geometry
 * already carries its own weight, and stroking a filled path would thicken it unevenly.
 *
 * The tile also carries its own visible label (hidden at row width, but present as the panel's
 * aria-label), so this stays aria-hidden.
 */
export function LibraryIcon({ className = '', size = 28 }: IconProps) {
  return (
    <svg
      viewBox="0 0 14 13"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {/* The 2x2 block of drawers, each an outline with a hollow handle slot. */}
      <path d="M7.5 8.5H4V5h3.5v3.5ZM5 7.5h1.5V6H5v1.5Z" />
      <path d="M7.5 13H4V9.5h3.5V13ZM5 12h1.5v-1.5H5V12Z" />
      <path d="M12 8.5H8.5V5H12v3.5ZM9.5 7.5H11V6H9.5v1.5Z" />
      <path d="M12 13H8.5V9.5H12V13ZM9.5 12H11v-1.5H9.5V12Z" />
      {/* The folder behind them: tabbed top-left, open along the right where the drawers sit. */}
      <path d="M3 12H1c-.265 0-.519-.106-.707-.293C.106 11.519 0 11.265 0 11V1C0 .735.106.481.293.293.481.106.735 0 1 0h3.793c.131 0 .261.026.383.076.121.05.231.124.324.217L7.207 2H13c.265 0 .519.106.707.293.187.188.293.442.293.707v4h-1V3H6.793l-2-2H1v10h2v1Z" />
    </svg>
  );
}
